import React, {useState, useMemo} from "react";
import styled from "styled-components";
import {CRAFTING_RECIPES, GameItems} from "../State/GameData";
import {useLocation} from "react-router-dom";
import {CraftingCard} from "./CraftingCard";
import {useInventory} from "../State/InventoryContext";
import {SearchInput} from "../SharedComponents/SearchInput";

type LocationState = {
    targetItemId?: string;
};

const C = {
    Page: styled.div`
      padding: 16px;
    `,
    SearchContainer: styled.div`
      margin-bottom: 20px;
    `,
    Recipes: styled.div`
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 16px;
    `,
    EmptyState: styled.div`
      grid-column: 1 / -1;
      text-align: center;
      padding: 40px 20px;
      color: #64748b;
      font-size: 14px;
    `,
    Subtitle: styled.p`
      color: #555;
      margin: 0 0 12px 0;
    `,
};

export function HandCrafting() {
    const location = useLocation();
    const {targetItemId} = (location.state as LocationState ?? {});
    const [focusedItemId, setFocusedItemId] = useState<string | undefined>(targetItemId);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const {hasTechnology, addToQueue} = useInventory();
    const recipeRequirements: Record<string, string> = {
        "craft-transport-belt": "tech-transport-belt",
        "craft-inserter": "tech-inserter",
        "craft-circuit-assembler": "tech-automation",
        "craft-steel-furnace": "tech-steel-furnace",
        "craft-science-pack2": "tech-steel-smelting",
    };

    const filteredRecipes = useMemo(() => {
        // First filter by technology requirements
        let recipes = CRAFTING_RECIPES.filter(recipe => {
            const requirement = recipeRequirements[recipe.id];
            return !requirement || hasTechnology(requirement);
        });

        // Then filter by search term
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            recipes = recipes.filter(recipe => {
                const recipeName = recipe.name.toLowerCase();
                const resultItemName = GameItems[recipe.result.itemId]?.name?.toLowerCase() || "";
                const ingredientNames = recipe.ingredients
                    .map(ing => GameItems[ing.itemId]?.name?.toLowerCase() || "")
                    .join(" ");

                return recipeName.includes(term) ||
                       resultItemName.includes(term) ||
                       ingredientNames.includes(term);
            });
        }

        return recipes;
    }, [searchTerm, hasTechnology]);

    const handleIngredientHover = (itemId?: string) => {
        setFocusedItemId(itemId ?? targetItemId);
    };

    const handleIngredientClick = (itemId: string) => {
        const element = document.getElementById(`craft-card-${itemId}`);
        if (element) {
            element.scrollIntoView({behavior: "smooth", block: "start"});
        }
        setFocusedItemId(itemId);
        setTimeout(() => setFocusedItemId(targetItemId), 900);
    };

    return <C.Page>
        <h2>Crafting</h2>
        <C.Subtitle>Add items to the crafting queue. Items will be crafted automatically at 25% speed.</C.Subtitle>
        <C.SearchContainer>
            <SearchInput
                placeholder="Search recipes, items, or ingredients..."
                value={searchTerm}
                onChange={setSearchTerm}
            />
        </C.SearchContainer>
        {filteredRecipes.length === 0 && searchTerm.trim() ? (
            <C.EmptyState>No recipes found matching "{searchTerm}"</C.EmptyState>
        ) : (
            <C.Recipes>
                {filteredRecipes.map(recipe => {
                    const isHighlighted = focusedItemId === recipe.result.itemId;
                    return <CraftingCard
                        key={recipe.id}
                        recipe={recipe}
                        isHighlighted={isHighlighted}
                        focusedItemId={focusedItemId}
                        onIngredientHover={handleIngredientHover}
                        onIngredientClick={handleIngredientClick}
                        onAddToQueue={addToQueue}
                    />;
                })}
            </C.Recipes>
        )}
    </C.Page>;
}
